// ABOUTME: The single source of truth for the reel <video>'s object-fit, by composition.
// ABOUTME: The player styles the surfaces with it and useVideoBox maps overlay anchors through it.

/**
 * SPEC-26 Phase 4b, revised 2026-09-16 — how the chapter film is fitted, per composition.
 *
 * DESKTOP is `cover`, always, and nothing here may change that: the desktop master is a 16:9 cut
 * and a desktop window is 16:9-ish, so `cover` crops essentially nothing.
 *
 * MOBILE is `contain`. This is a reversal of the 2026-08-13 client direction ("stay on `cover` —
 * the dead space costs more than the cropped edges"), and the reason is that the ruling was made
 * about a different asset. At the time there was one 16:9 master for both compositions, so
 * `contain` on a 390x844 handset meant a 390x219 strip with ~250px of dead space above it — an
 * obviously bad trade. SPEC-61 shipped a genuine PORTRAIT mobile master (1080x1920), which changes
 * the arithmetic completely:
 *
 *   cover   — fills the screen, hiding 96px off EACH side: 17.9% of the frame's width. Measured
 *             against the real media (ffmpeg, 2026-09-16), that clips the straps of the outer two
 *             watches in every lineup shot — the CAROUSSEL hold, the colour roll's resting frame,
 *             and all seven variant slots.
 *   contain — nothing is ever cropped, at the cost of a 151px letterbox (17.9% of viewport
 *             height), ~75px top and bottom.
 *
 * Owner direction 2026-09-16: `contain`. It also removes a second defect — SPEC-61 had applied
 * `contain` to `collection_roll` ALONE, so the roll rendered at a different scale than the
 * chapters it cuts between, which read on screen as the picture popping size at the seam.
 *
 * The escape hatch survives, inverted: `REACT_APP_REEL_MOBILE_FIT=cover` (build-time, same
 * mechanism as `REACT_APP_VAD_TUNER`) puts mobile back on the old behaviour without a code change.
 * The durable fix is unchanged — portrait masters for every chapter, at which point the two
 * compositions agree and this module can go. See DESIGN.md §6.5.
 *
 * ORIENTATION OVERRIDE (2026-09-16). `contain` is only ever the right answer for a film whose own
 * shape roughly matches the screen's. A LANDSCAPE master on a portrait handset letterboxes to a
 * 390x219 strip with ~250px of dead space — the exact trade the 2026-08-13 ruling rejected, and
 * what a guest actually saw when the mobile intro fell back to the 16:9 desktop film: a thin band
 * of film floating in black. So a landscape source is always `cover`, whatever the composition
 * default says. Crop a wide film; never shrink it to a ribbon.
 *
 * `mediaAspect` is the video's INTRINSIC ratio (videoWidth / videoHeight), which the player reads
 * off each surface once its metadata lands. Omit it to get the answer for the composition's
 * authored asset shape — that is what overlay anchor maths wants, since the Figma frames are
 * portrait on mobile by construction. Unknown resolves to the default rather than guessing, and
 * metadata always lands before the first frame paints, so no letterbox is ever briefly visible.
 *
 * Read per call rather than at module load so a test can drive both arms; CRA inlines the literal
 * at build time either way.
 *
 * CROP VERTICALLY, NEVER HORIZONTALLY (2026-09-16). The content is a horizontal lineup of watches,
 * so losing width is the one thing that actually costs us — it slices the outer two dials. Losing
 * height costs nothing, because the composition is centred and the masters are authored with a
 * vertical safe margin. That gives one rule covering every master shape we might be handed:
 *
 *   master TALLER than the screen  → `cover`. Filling crops the top and bottom, which is the safe
 *                                    axis. This is the case for a 9:20 master (0.45) on any phone.
 *   master WIDER than the screen   → `contain`. Filling would crop width, so letterbox instead.
 *                                    This is today's 9:16 master (0.5625) on every phone.
 *
 * Deliberately NOT a behaviour change on the current pair: every real handset is narrower than
 * 0.5625, so a 9:16 master is always the "wider" case and always resolves `contain`, exactly as it
 * did before. `reelVideoFit.test.js` pins that against the full Chrome device-preset list. What it
 * does change is the day a 9:20 master is uploaded — mobile starts filling the screen on its own,
 * with no code change and no deploy, the same way the `_mobile` matched-pair naming already works.
 *
 * Both aspects optional and independently so: omit `mediaAspect` (what `useVideoBox` does — it maps
 * anchors against the AUTHORED frame, not whatever film happens to be loaded) or omit
 * `viewportAspect` and the answer falls back to the composition default rather than guessing.
 *
 * @param {'mobile'|'desktop'|string} breakpoint
 * @param {number} [mediaAspect] intrinsic width/height of the film on that surface
 * @param {number} [viewportAspect] width/height of the box it is being fitted into
 * @returns {'cover'|'contain'}
 */
export function reelVideoFit(breakpoint, mediaAspect, viewportAspect) {
  if (breakpoint !== 'mobile') return 'cover';
  if (process.env.REACT_APP_REEL_MOBILE_FIT === 'cover') return 'cover';
  if (!Number.isFinite(mediaAspect)) return 'contain';
  if (mediaAspect > 1) return 'cover';
  if (!Number.isFinite(viewportAspect) || viewportAspect <= 0) return 'contain';
  return mediaAspect <= viewportAspect ? 'cover' : 'contain';
}

/** Back-compat alias for the pre-2026-09-16 name; the mobile arm of {@link reelVideoFit}. */
export function mobileVideoFit() {
  return reelVideoFit('mobile');
}
